import {
	DateTimeInput,
	FileDropArea,
	FileInput,
	Input,
	RadioInput,
	CheckboxInput,
	SwitchInput,
	SelectInput,
	TextArea,
	InputGroup,
	Box,
	Inline,
	Stack,
	AsyncSelectProps,
	useId,
	Text,
	Icons,
	DurationInput,
	InlineSelectInput,
	Dropdown,
	ColorInput,
} from "@sembark-travel/ui-base"
import { Optional } from "utility-types"
import type { AnySchema, ValidationError } from "yup"
import { mixed as mixedValidator } from "yup"
import {
	Form as ReactFinalForm,
	Field,
	useField,
	FormSpy,
	useForm,
	useFormState,
} from "react-final-form"
import type {
	FormProps,
	FieldRenderProps,
	FormRenderProps,
} from "react-final-form"
import arrayMutators from "final-form-arrays"
import {
	FieldArray as ReactFinalFormFieldArray,
	FieldArrayProps,
	useFieldArray,
	FieldArrayRenderProps as ReactFinalFormFieldArrayRenderProps,
} from "react-final-form-arrays"
import { ErrorMessage, FieldArrayErrorMessage } from "./ErrorMessage"

import { FORM_ERROR, setIn, getIn, type FormApi, ARRAY_ERROR } from "final-form"
import { MarkdownEditor } from "@sembark-travel/ui-markdown"
import {
	useRef,
	useEffect,
	useCallback,
	type KeyboardEvent,
	type FormEvent,
} from "react"
import type { AxiosError } from "axios"
import {
	TCurrencyCode,
	currencyExchangeRatioToString,
} from "@sembark-travel/money"
import {
	addUnit,
	endOf,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import {
	isFieldOfTypeArraySchema,
	runAsyncValidation,
	type TAsyncValidatorState,
} from "./utils"

export type {
	FormProps,
	FormRenderProps,
	FormApi,
	ReactFinalFormFieldArrayRenderProps,
}

const FIELD_ARRAY_KEY = "__id" as const

export {
	Field,
	useField,
	FormSpy,
	useForm,
	useFormState,
	arrayMutators,
	getIn as getFormValueForKey,
}

const POWER_10_OF_10 = Math.pow(10, 10)

function generateID(): number {
	let id = 0
	do {
		id = Math.floor(Math.random() * POWER_10_OF_10)
	} while (!id)
	return id
}

/**
 * Append key to an item in a field array to track the keys
 */
export function addKeyToFieldArrayItem<T extends object>(value: T) {
	return {
		...value,
		[FIELD_ARRAY_KEY]: generateID(),
	}
}

export function getFieldArrayErrorName(name: string) {
	return `${name.replace(/\[(\d+)\]/g, "_$1_")}_${ARRAY_ERROR}`
}

/**
 * Get the key for an item in a field array for given name
 */
export function getFieldArrayItemKey(form: FormApi, name: string) {
	const key = getIn(form.getState().values, `${name}.${FIELD_ARRAY_KEY}`)
	if (!key) {
		console.error({ name, values: form.getState().values })
		// fallback to name
		return `${name}.${FIELD_ARRAY_KEY}`
	}
	return key
}

export function useFieldArrayLength(name: string): number {
	const { fields } = useFieldArray(name, {
		subscription: { length: true },
	})
	return fields.length || 0
}

export function FieldArray<
	FieldValue = unknown,
	T extends HTMLElement = HTMLElement,
>(props: FieldArrayProps<FieldValue, T>) {
	return (
		<>
			<ReactFinalFormFieldArray subscription={{}} {...props} />
			<FieldArrayErrorMessage name={props.name} />
		</>
	)
}

export function Form<FormValues = Record<string, unknown>>(
	props: FormProps<FormValues>
) {
	return <ReactFinalForm<FormValues> keepDirtyOnReinitialize {...props} />
}

export function TextInputField({
	name,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	Omit<React.ComponentProps<typeof Input>, "label">) {
	return (
		<FieldGroup<string | number> name={name} {...props}>
			{({ input, meta, ...otherProps }) => <Input {...input} {...otherProps} />}
		</FieldGroup>
	)
}

export function useCurrencyPairRatioFieldValue(
	name: string,
	baseCurrency: TCurrencyCode,
	counterCurrency: TCurrencyCode
) {
	const { value, onChange } = useFieldValue<number | "">(
		`${name}.${baseCurrency}.${counterCurrency}`
	)
	const form = useForm()
	useEffect(() => {
		if (baseCurrency === counterCurrency && Number(value) !== 1) {
			const h = setTimeout(() => {
				form.change(`${name}.${baseCurrency}.${counterCurrency}`, 1)
			})
			return () => clearTimeout(h)
		}
		return () => undefined
	}, [baseCurrency, counterCurrency, value, form, name])
	return { value, onChange }
}

export function GetCurrencyPairRatioFieldValue({
	name,
	baseCurrency,
	counterCurrency,
	children,
}: {
	name: string
	baseCurrency: TCurrencyCode
	counterCurrency: TCurrencyCode
	children: (
		props: ReturnType<typeof useCurrencyPairRatioFieldValue>
	) => React.ReactNode
}) {
	const props = useCurrencyPairRatioFieldValue(
		name,
		baseCurrency,
		counterCurrency
	)
	return <>{children(props)}</>
}

export function CurrencyPairInputField({
	name,
	baseCurrency,
	counterCurrency,
	label,
	defaultValue,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	React.ComponentProps<typeof Input> & {
		baseCurrency: TCurrencyCode
		counterCurrency: TCurrencyCode
	}) {
	if (label !== "") {
		label = `${baseCurrency}/${counterCurrency}`
	}
	const rateFieldName = `${name}.${baseCurrency}.${counterCurrency}`
	const { value: rate, onChange: changeRate } = useCurrencyPairRatioFieldValue(
		name,
		baseCurrency,
		counterCurrency
	)
	const initialRate = useRef(rate)
	useEffect(() => {
		if (!initialRate.current && defaultValue) {
			initialRate.current = Number(defaultValue as string)
			setTimeout(() => {
				changeRate(initialRate.current)
			})
		}
	}, [initialRate, defaultValue, changeRate])
	return (
		<FieldGroup<string | number>
			name={rateFieldName}
			{...props}
			label={label}
			secondaryLabel={
				defaultValue ? (
					<Box display="inlineBlock">
						<Text
							as="a"
							color="accent"
							href="#"
							title="Click to reset to defaults"
							onClick={
								((e) => {
									e.preventDefault()
									changeRate(Number(defaultValue as string))
								}) as React.MouseEventHandler<HTMLAnchorElement>
							}
						>
							Default/Rate: {defaultValue}
						</Text>{" "}
						{Number(rate || 0) !== Number(defaultValue || 0) ? (
							<Icons.AttentionSolid
								color="warning"
								title="Current and Default Rate Mismatch"
							/>
						) : null}
					</Box>
				) : null
			}
			help={({ value }) => (
				<Text>
					1 {baseCurrency} ={" "}
					{value !== ""
						? currencyExchangeRatioToString(Number(value || 0))
						: "?"}{" "}
					{counterCurrency}
				</Text>
			)}
		>
			{({ input, meta, ...otherProps }) => (
				<Input min={0} {...input} {...otherProps} type="number" />
			)}
		</FieldGroup>
	)
}

export function MoneyInputField({
	name,
	currencyFieldName,
	currencies,
	disabled = false,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	React.ComponentProps<typeof Input> & {
		currencyFieldName: string
		currencies?: Array<string>
	}) {
	return (
		<FieldGroup<string | number>
			name={`${name}`}
			{...props}
			secondaryLabel={
				currencies?.length ? (
					<InlineSelectInputField
						disabled={disabled}
						name={currencyFieldName}
						noCaret
					>
						{currencies.map((o) => (
							<option value={o} key={o}>
								{o}
							</option>
						))}
					</InlineSelectInputField>
				) : (
					props.secondaryLabel
				)
			}
		>
			{({ input, meta, ...otherProps }) => (
				<Input
					min={0}
					disabled={disabled}
					placeholder="e.g. 3000"
					{...input}
					{...otherProps}
					type="number"
				/>
			)}
		</FieldGroup>
	)
}

function handleTwoDecimalsKeyDown(e: KeyboardEvent<HTMLInputElement>) {
	if (
		e.key === "Backspace" ||
		e.key === "Delete" ||
		e.key === "Tab" ||
		e.key === "Escape" ||
		e.key === "Enter" ||
		e.key.startsWith("Arrow") ||
		e.key === "Home" ||
		e.key === "End" ||
		e.ctrlKey ||
		e.metaKey ||
		e.altKey
	) {
		return
	}

	if (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") {
		e.preventDefault()
		return
	}

	const input = e.currentTarget
	const val = input.value

	if (e.key === "." || e.key === ",") {
		if (val.includes(".")) {
			e.preventDefault()
		}
		return
	}

	if (/^\d$/.test(e.key)) {
		const decimalIndex = val.indexOf(".")
		if (decimalIndex !== -1) {
			let hasSelection = false
			let selStart: number | null = null
			try {
				selStart = input.selectionStart
				hasSelection =
					input.selectionStart != null &&
					input.selectionStart !== input.selectionEnd
			} catch {
				// selectionStart is not supported on input[type="number"] in some browsers
			}

			if (!hasSelection) {
				if (selStart == null || selStart > decimalIndex) {
					const decimals = val.slice(decimalIndex + 1)
					if (decimals.length >= 2) {
						e.preventDefault()
					}
				}
			}
		}
	}
}

function handleTwoDecimalsInput(e: FormEvent<HTMLInputElement>) {
	const input = e.currentTarget
	const val = input.value
	const decimalIndex = val.indexOf(".")
	if (decimalIndex !== -1 && val.length - decimalIndex - 1 > 2) {
		input.value = val.slice(0, decimalIndex + 3)
	}
}

export function PercentageInputField({
	name,
	style,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	Omit<React.ComponentProps<typeof Input>, "type">) {
	return (
		<FieldGroup<string | number>
			name={name}
			type="number"
			{...props}
			style={{ width: "90px", ...style }}
		>
			{({ input, meta, ...otherProps }) => (
				<Inline alignItems="center">
					<Input
						placeholder="00.00"
						min={0}
						max={100}
						step="0.01"
						{...input}
						{...otherProps}
						className={otherProps.className}
						onKeyDown={(e) => {
							handleTwoDecimalsKeyDown(e)
							if (!e.defaultPrevented && otherProps.onKeyDown) {
								otherProps.onKeyDown(e)
							}
						}}
						onInput={(e) => {
							handleTwoDecimalsInput(e)
							if (otherProps.onInput) {
								otherProps.onInput(e)
							}
						}}
					/>
					<Box paddingX="1">%</Box>
				</Inline>
			)}
		</FieldGroup>
	)
}

export function TextAreaInputField({
	name,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	React.ComponentProps<typeof TextArea>) {
	return (
		<FieldGroup<string> name={name} {...props}>
			{({ input, meta, ...otherProps }) => (
				<TextArea {...input} {...otherProps} />
			)}
		</FieldGroup>
	)
}

/**
 * Select Input Field, as inline with Text Visibility
 */
export function InlineSelectInputField({
	name,
	children,
	...props
}: Omit<
	React.ComponentProps<typeof FieldGroup>,
	"children" | "label" | "help" | "secondaryLabel"
> &
	React.ComponentProps<typeof InlineSelectInput>) {
	return (
		<Box display="inlineBlock">
			<FieldGroup<string> name={name} {...props}>
				{({ input, meta, ...otherProps }) => (
					<InlineSelectInput {...input} {...otherProps}>
						{children}
					</InlineSelectInput>
				)}
			</FieldGroup>
		</Box>
	)
}

export function SelectInputField({
	name,
	children,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	React.ComponentProps<typeof SelectInput>) {
	return (
		<FieldGroup<string> name={name} {...props}>
			{({ input, meta, ...otherProps }) => (
				<SelectInput {...input} {...otherProps}>
					{children}
				</SelectInput>
			)}
		</FieldGroup>
	)
}

export function DatePickerField({
	name,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	React.ComponentProps<typeof DateTimeInput>) {
	return (
		<FieldGroup<Date | undefined> name={name} {...props}>
			{({ input, meta, ...otherProps }) => (
				<DateTimeInput {...input} {...otherProps} />
			)}
		</FieldGroup>
	)
}

type TDateRangePreset =
	| "Today"
	| "Yesterday"
	| "This Week"
	| "This Month"
	| "Last Month"
	| "Next 7 Days"
	| "Prev 7 Days"

function getRangeForPreset(preset: TDateRangePreset): [Date, Date] {
	const interval: [Date, Date] = [
		startOf(new Date(), "day"),
		endOf(new Date(), "day"),
	]
	switch (preset) {
		case "Today":
			interval[0] = startOf(new Date(), "day")
			interval[1] = endOf(new Date(), "day")
			break
		case "Yesterday":
			interval[0] = startOf(subtractUnit(new Date(), 1, "day"), "day")
			interval[1] = endOf(interval[0], "day")
			break
		case "This Week":
			interval[0] = startOf(new Date(), "week")
			interval[1] = endOf(new Date(), "week")
			break
		case "This Month":
			interval[0] = startOf(new Date(), "month")
			interval[1] = endOf(new Date(), "month")
			break
		case "Last Month":
			interval[0] = subtractUnit(startOf(new Date(), "month"), 1, "month")
			interval[1] = endOf(interval[0], "month")
			break
		case "Next 7 Days":
			interval[0] = addUnit(startOf(new Date(), "day"), 1, "day")
			interval[1] = addUnit(interval[0], 6, "day")
			break
		case "Prev 7 Days":
			interval[1] = subtractUnit(startOf(new Date(), "day"), 1, "day")
			interval[0] = subtractUnit(interval[1], 6, "day")
			break
	}
	return interval
}

const DEFAULT_DATE_RANGE_PRESETS: Array<TDateRangePreset> = [
	"Today",
	"Yesterday",
	"This Week",
	"This Month",
	"Last Month",
]

export function DateRangePickerField({
	fromName,
	toName,
	label,
	secondaryLabel,
	rangePresets = DEFAULT_DATE_RANGE_PRESETS,
	dateFormat = "DD MMM'YY",
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children" | "name" | "help"> &
	Omit<React.ComponentProps<typeof DateTimeInput>, "name"> & {
		fromName: string
		toName: string
		rangePresets?: Array<TDateRangePreset> | null
	}) {
	const id = `field_${fromName}`
	const form = useForm()
	return (
		<Stack gap="1">
			{label ? (
				<Inline justifyContent="between">
					<Inline gap="2" alignItems="baseline">
						<label htmlFor={id}>{label}</label>
						{secondaryLabel ? (
							<Box fontSize="sm" color="muted">
								({secondaryLabel})
							</Box>
						) : null}
					</Inline>
					{rangePresets && rangePresets?.length ? (
						<Dropdown alignRight>
							<Dropdown.ToggleButton inline>
								<Icons.Calendar />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								{rangePresets.map((label) => (
									<Dropdown.MenuItem
										key={label}
										onClick={(e: React.MouseEvent) => {
											// the menu item is an <a href="#">, so without this the
											// browser follow it and jump to the top of the page
											e.preventDefault()
											const interval = getRangeForPreset(label)
											form.batch(() => {
												form.change(fromName, interval[0])
												form.change(toName, interval[1])
											})
										}}
									>
										{label}
									</Dropdown.MenuItem>
								))}
							</Dropdown.Menu>
						</Dropdown>
					) : null}
				</Inline>
			) : null}
			<Inline alignItems="baseline">
				<FieldGroup<Date | undefined> name={fromName} {...props}>
					{({ input, meta, ...otherProps }) => (
						<DateTimeInput {...input} dateFormat={dateFormat} {...otherProps} />
					)}
				</FieldGroup>
				<Box>
					<Icons.ArrowLeft rotate="180" />
				</Box>
				<FieldGroup<Date | undefined> name={toName} {...props}>
					{({ input, meta, ...otherProps }) => (
						<DateTimeInput
							{...input}
							dateFormat={dateFormat}
							{...otherProps}
							rightAlign
						/>
					)}
				</FieldGroup>
			</Inline>
		</Stack>
	)
}

export function FileInputField({
	name,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	React.ComponentProps<typeof FileInput>) {
	const id = useId()
	const fileNameInputName = `file_name_${id}`
	return (
		<FieldGroup<File | FileList | null> name={name} {...props}>
			{({ input, meta, ...otherProps }) => {
				const { value, onChange, ...inputProps } = input
				return (
					<FileInput
						{...inputProps}
						{...otherProps}
						// A file set on the form from outside the input, such as a
						// drop elsewhere, shows in it too. A caller with its own
						// onChange keeps the file elsewhere, so the input tracks it.
						files={props.onChange ? undefined : value || null}
						name={fileNameInputName}
						onBlur={() => {
							input.onBlur()
						}}
						onChange={(files) => {
							const file = props.multiple ? files : files ? files[0] : null
							props.onChange ? props.onChange(files || null) : onChange(file)
						}}
					/>
				)
			}}
		</FieldGroup>
	)
}

/**
 * A FileDropArea that puts a file dropped on it, or pasted into it, into the
 * form field `name`. `onFiles` runs first, to ready what shows the field — an
 * attachment field kept hidden until needed, say.
 */
export function FileDropAreaField({
	name,
	onFiles,
	...props
}: Omit<React.ComponentProps<typeof FileDropArea>, "multiple" | "onFiles"> & {
	name: string
	onFiles?: (file: File) => void
}) {
	const form = useForm()

	return (
		<FileDropArea
			{...props}
			onFiles={(files) => {
				onFiles?.(files[0])
				form.change(name, files[0])
			}}
		/>
	)
}

export function SelectField<
	SelectProps extends Optional<AsyncSelectProps, "fetch">,
>({
	name,
	select,
	multiple,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	SelectProps & { select: React.ComponentType<SelectProps> }) {
	const Component: React.ComponentType<SelectProps> = select
	return (
		<FieldGroup name={name} {...props}>
			{({ input, meta, ...otherProps }) => {
				const props = {
					multiple,
					...input,
					...otherProps,
				} as never as SelectProps
				return <Component {...props} />
			}}
		</FieldGroup>
	)
}

function numberParser(value: unknown) {
	if (value === "") return undefined
	if (typeof value === "string") {
		return Number(value)
	}
	return value
}

// Stable noop — avoids a conditional hook call when asyncValidate is absent.
const ASYNC_VALIDATE_NOOP = (): Promise<undefined> => Promise.resolve(undefined)

export function FieldGroup<FieldValue = unknown>({
	hideErrors,
	help,
	label,
	secondaryLabel,
	asyncValidate,
	asyncValidateWait = 300,
	validatingText = "Validating",
	...props
}: {
	name: string
	hideErrors?: boolean
	help?: React.ReactNode | ((props: { value: FieldValue }) => React.ReactNode)
	label?: React.ReactNode
	secondaryLabel?: React.ReactNode
	required?: boolean
	type?: string
	// Resolves to an error message, or undefined when the value is valid. It is
	// debounced by `asyncValidateWait` ms and blocks the form submission while an
	// error is returned.
	asyncValidate?: (
		value: unknown,
		allValues: object
	) => Promise<string | undefined>
	asyncValidateWait?: number
	validatingText?: string
	children: (props: FieldRenderProps<FieldValue>) => React.ReactNode
	style?: React.CSSProperties
}) {
	const { name } = props

	const debouncedValidator = useAsyncFieldValidator(
		asyncValidate || ASYNC_VALIDATE_NOOP,
		asyncValidateWait
	)

	const {
		meta: { error, touched, submitError, validating, valid },
	} = useField<FieldValue>(name, {
		subscription: {
			error: true,
			touched: true,
			submitError: true,
			validating: true,
			valid: true,
		},
	})
	const validated =
		!!asyncValidate && touched && !validating && valid && !submitError
	const id = `field_${name}`
	return (
		<InputGroup hasError={!!(touched && error) || submitError}>
			<Stack gap="1">
				{label ? (
					<Inline gap="2" alignItems="baseline">
						<label htmlFor={id}>{label}</label>
						{secondaryLabel ? (
							<Box fontSize="sm" color="muted">
								({secondaryLabel})
							</Box>
						) : null}
					</Inline>
				) : null}
				{/* same width limits as the input, so the spinner sits inside it */}
				<Box
					position="relative"
					style={{ width: props.style?.width, maxWidth: props.style?.maxWidth }}
				>
					<Field<FieldValue>
						subscription={{ value: true }}
						id={id}
						data-testid={id}
						{...(props.type === "number"
							? {
									parse: numberParser as never,
								}
							: {})}
						{...props}
						validate={asyncValidate ? debouncedValidator : undefined}
					/>
					{validating || validated ? (
						<Box
							position="absolute"
							style={{
								top: "50%",
								right: "0.5rem",
								transform: "translateY(-50%)",
							}}
							color={validating ? "muted" : "success"}
						>
							{validating ? (
								<Icons.Spinner />
							) : (
								<Icons.OkCircleSolid title="Available" />
							)}
						</Box>
					) : null}
				</Box>
				{help ? <Help<FieldValue> name={name} help={help} /> : null}
				{validating ? (
					<Text color="muted" fontSize="sm">
						{validatingText}...
					</Text>
				) : null}
				{!hideErrors ? <ErrorMessage name={name} /> : null}
			</Stack>
		</InputGroup>
	)
}

function Help<FieldValue = unknown>({
	help,
	name,
}: {
	name: string
	help: React.ReactNode | ((props: { value: FieldValue }) => React.ReactNode)
}) {
	return typeof help === "function" ? (
		<GetFieldValue<FieldValue> name={name}>
			{({ value }) => (
				<Box
					color="muted"
					fontSize="sm"
					letterSpacing="wide"
					fontWeight="normal"
				>
					{help({ value })}
				</Box>
			)}
		</GetFieldValue>
	) : (
		<Box color="muted" fontSize="sm" letterSpacing="wide" fontWeight="normal">
			{help}
		</Box>
	)
}

export function RadioInputField({
	hideErrors,
	help,
	label,
	secondaryLabel,
	data,
	id,
	value,
	expandLabel = false,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	React.ComponentProps<typeof RadioInput> & { expandLabel?: boolean }) {
	const { name } = props
	const {
		meta: { error, touched, submitError },
	} = useField(name, {
		subscription: { error: true, touched: true, submitError: true },
	})
	id = id || `field_${name}_${value ? String(value) : "set_id"}`
	return (
		<InputGroup hasError={!!(touched && error) || submitError}>
			<Stack gap="1">
				<Inline gap="2">
					<Box>
						<Field
							type="radio"
							subscription={{ value: true }}
							id={id}
							data-testid={id}
							value={value}
							{...props}
						>
							{({ input, meta, ...otherProps }) => (
								<RadioInput {...input} {...otherProps} />
							)}
						</Field>
					</Box>
					{label || help ? (
						<Stack gap="1" {...(expandLabel ? { flex: "1" } : {})}>
							{label ? (
								<Inline gap="2" alignItems="center">
									<Box
										as="label"
										htmlFor={id}
										cursor={props.disabled ? "disabled" : undefined}
									>
										{label}
									</Box>
									{secondaryLabel ? (
										<Box fontSize="sm" color="muted">
											({secondaryLabel})
										</Box>
									) : null}
								</Inline>
							) : null}
							{help ? <Help name={name} help={help} /> : null}
						</Stack>
					) : null}
				</Inline>
				{!hideErrors ? <ErrorMessage name={name} /> : null}
			</Stack>
		</InputGroup>
	)
}

export function CheckboxInputField({
	hideErrors,
	help,
	label,
	secondaryLabel,
	data,
	id,
	value,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	Omit<React.ComponentProps<typeof CheckboxInput>, "label">) {
	const { name } = props
	const {
		meta: { error, touched, submitError },
	} = useField(name, {
		subscription: { error: true, touched: true, submitError: true },
	})
	id = id || `field_${name}_${value ? String(value) : "set_id"}`
	return (
		<InputGroup hasError={!!(touched && error) || submitError}>
			<Stack gap="1">
				<Inline gap="2">
					<Box>
						<Field
							type="checkbox"
							subscription={{ value: true }}
							id={id}
							data-testid={id}
							value={value}
							{...props}
						>
							{({ input, meta, ...otherProps }) => (
								<CheckboxInput {...input} {...otherProps} />
							)}
						</Field>
					</Box>
					{label || help ? (
						<Stack gap="1">
							{label ? (
								<Inline gap="2">
									<label htmlFor={id}>{label}</label>
									{secondaryLabel ? (
										<Box fontSize="sm" color="muted">
											({secondaryLabel})
										</Box>
									) : null}
								</Inline>
							) : null}

							{help ? <Help name={name} help={help} /> : null}
						</Stack>
					) : null}
				</Inline>
				{!hideErrors ? <ErrorMessage name={name} /> : null}
			</Stack>
		</InputGroup>
	)
}

export function isTruthy(value: unknown): boolean {
	if (!value) return false
	if (Array.isArray(value)) return value.length > 0
	const str = String(value).toLowerCase()
	return str === "true" || str === "1" || str === "on"
}

export function switchValidator() {
	return mixedValidator().oneOf(
		[0, "0", false, "false", "off", 1, true, "1", "true", "on"],
		"Invalid selected value"
	)
}

export function SwitchInputField({
	hideErrors,
	help,
	label,
	labelStyle,
	alignLabelCenter,
	secondaryLabel,
	data,
	id,
	value,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	Omit<React.ComponentProps<typeof SwitchInput>, "label"> & {
		labelStyle?: React.CSSProperties
		alignLabelCenter?: boolean
	}) {
	const { name } = props
	const {
		meta: { error, touched, submitError },
	} = useField(name, {
		subscription: { error: true, touched: true, submitError: true },
	})
	id = id || `field_${name}_${value ? String(value) : "set_id"}`
	return (
		<InputGroup hasError={!!(touched && error) || submitError}>
			<Stack gap="1">
				<Inline gap="2">
					<Box>
						<Field
							subscription={{ value: true }}
							id={id}
							data-testid={id}
							{...props}
						>
							{({ input, meta, ...otherProps }) => {
								return (
									<SwitchInput
										{...input}
										{...otherProps}
										checked={isTruthy(input.value)}
									/>
								)
							}}
						</Field>
					</Box>
					<Stack
						gap="1"
						justifyContent={alignLabelCenter ? "center" : undefined}
					>
						{label ? (
							<Inline
								gap="2"
								alignItems={alignLabelCenter ? "center" : undefined}
							>
								<Text
									as="label"
									htmlFor={id}
									cursor={props.disabled ? "disabled" : undefined}
									style={labelStyle}
								>
									{label}
								</Text>
								{secondaryLabel ? (
									<Box fontSize="sm" color="muted">
										({secondaryLabel})
									</Box>
								) : null}
							</Inline>
						) : null}
						{help ? <Help name={name} help={help} /> : null}
					</Stack>
				</Inline>
				{!hideErrors ? <ErrorMessage name={name} /> : null}
			</Stack>
		</InputGroup>
	)
}

// To be passed to React Final Form
export function validateFormValues<Type>(schema: AnySchema<Type>) {
	return async function validate<Values>(values: Values) {
		try {
			await schema.validate(values, { abortEarly: false })
			return
		} catch (e: unknown) {
			const err = e as ValidationError
			if (!err || !err.inner) {
				return
			}
			const errors = err.inner.reduce((formError, innerError) => {
				if (innerError.path && innerError.message) {
					const isArrayAtPath = isFieldOfTypeArraySchema(
						innerError.path,
						schema
					)
					if (isArrayAtPath) {
						// The array field level errors are stored in `errorName`, instead of `name` to store both array field level errors
						// This allows us to store the nested items errors in their respective field
						// names and store the array level errors in the array field level
						// (`array_name_error` and `array_name[0].description`)
						// if we stored the errors in same name, then (`array_name`: Error and `array_name[0].description`: Error) are not
						// possible as the strings to array conversion will fail
						return setIn(
							formError,
							getFieldArrayErrorName(innerError.path),
							innerError.message
						)
					} else {
						return setIn(formError, innerError.path, innerError.message)
					}
				}
				return formError
			}, {})
			return errors
		}
	}
}

export const withServerErrors: <FormValues>(
	arg: FormProps<FormValues>["onSubmit"]
) => FormProps<FormValues>["onSubmit"] = function withServerErrors(
	handleSubmit
) {
	return async (values, ...args) => {
		return new Promise((resolve) => {
			Promise.resolve(handleSubmit(values, ...args))
				.then(resolve)
				.catch((err) => {
					const error = err as AxiosError
					const e = (error.response && error.response.data) as
						| undefined
						| { message: string; errors?: { [key: string]: Array<string> } }
					const message = !e ? error.message : e.message
					const fieldErrors = e?.errors
						? Object.keys(e.errors).reduce(
								(errors: { [key: string]: string }, name: string) => {
									if (e.errors) {
										errors[name] = e.errors[name].join(", ")
									}
									return errors
								},
								{}
							)
						: {}
					resolve({
						[FORM_ERROR]: message,
						...(fieldErrors || {}),
					})
				})
		})
	}
}

export function useFieldValue<FieldValue = unknown>(name: string) {
	const {
		input: { value, onChange },
	} = useField<FieldValue>(name, {
		subscription: { value: true },
	})
	return { value, onChange }
}

export function GetFieldValue<FieldValue = unknown>({
	name,
	children,
}: {
	name: string
	children: (props: {
		value: FieldValue
		onChange: (val: FieldValue) => void
	}) => React.ReactNode
}) {
	const { value, onChange } = useFieldValue<FieldValue>(name)
	return <>{children({ value, onChange })}</>
}

export function MarkdownInputField({
	name,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	React.ComponentProps<typeof MarkdownEditor>) {
	return (
		<FieldGroup<string | undefined> name={name} {...props}>
			{({ input, meta, ...otherProps }) => (
				<MarkdownEditor {...input} {...otherProps} name={name} />
			)}
		</FieldGroup>
	)
}

export function DurationInputField({
	name,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	Omit<React.ComponentProps<typeof DurationInput>, "onChange">) {
	return (
		<FieldGroup<string> name={name} {...props}>
			{({ input, meta, ...otherProps }) => (
				<DurationInput {...input} {...otherProps} />
			)}
		</FieldGroup>
	)
}
export function ColorInputField({
	name,
	presetColors,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	Omit<React.ComponentProps<typeof ColorInput>, "onChange"> & {
		presetColors?: Array<string> | false
	}) {
	return (
		<FieldGroup<string> name={name} {...props}>
			{({ input, meta, ...otherProps }) => (
				<ColorInput {...input} {...otherProps} presetColors={presetColors} />
			)}
		</FieldGroup>
	)
}

/**
 * React hook that creates a stable, debounced async field validator for use
 * with the `validate` prop on any field component (`TextInputField`, etc.).
 *
 * Uses a ref to always call the latest `fn` (no stale closures), while keeping
 * the debounced wrapper reference stable across renders (timer persists).
 * Only resets the debounce when `wait` changes.
 *
 * Usage:
 *   const validateEmail = useAsyncFieldValidator(async (value: string) => {
 *     if (!value) return undefined
 *     const msg = await checkEmailExists(value)
 *     return msg  // undefined = valid, string = error message
 *   }, 500)
 *
 *   <TextInputField name="email" validate={validateEmail} />
 */
export function useAsyncFieldValidator<V>(
	fn: (value: V, allValues: object) => Promise<string | undefined>,
	wait = 300
): (
	value: unknown,
	allValues: object
) => string | undefined | Promise<string | undefined> {
	// Single ref for all mutable state — never recreated, survives every render.
	// Typed as TAsyncValidatorState so it stays in sync with runAsyncValidation
	// without duplicating the state shape here.
	const stateRef = useRef<TAsyncValidatorState>({
		fn: fn as TAsyncValidatorState["fn"],
		wait,
		timer: undefined,
		lastValue: Symbol("unset"), // unique sentinel, never equals a real field value
		lastResolve: undefined,
		lastPromise: undefined,
		lastResult: undefined,
		isResolved: false,
	})

	// Keep fn and wait current after every render (runs outside of render — safe ref write)
	useEffect(() => {
		stateRef.current.fn = fn as TAsyncValidatorState["fn"]
		stateRef.current.wait = wait
	})

	// useCallback body runs only when the validator is invoked (outside of render).
	// Empty deps: stateRef is a stable object for the lifetime of the component.
	return useCallback(
		(
			value: unknown,
			allValues: object
		): string | undefined | Promise<string | undefined> =>
			runAsyncValidation(stateRef.current, value, allValues),
		[] // stateRef identity never changes — no deps needed
	)
}
